import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { CodeMirrorInput } from 'browser/components/atomic-elements/atoms/input/code-mirror-input'
import { Modal } from 'browser/components/atomic-elements/atoms/modal'
import { EntitySelectField } from 'browser/components/atomic-elements/molecules/fields/select-field/entity-select-field'
import { SelectField } from 'browser/components/atomic-elements/molecules/fields/select-field'
import { EntityPreviewCard } from 'browser/components/atomic-elements/organisms/entity/entity-preview-card/entity-preview-card'
import OverlayManager from 'browser/components/atomic-elements/organisms/overlay-manager/overlay-manager'
import { JSONSelectFactory } from 'browser/components/json-elements/higher-order-components/json-select-factory'
import { Entity } from 'shared-libs/models/entity'

const UIEntitySelectField = JSONSelectFactory(EntitySelectField)
const UISelectField = JSONSelectFactory(SelectField)

const PAYLOAD_CONFIG_PATH = 'core_integrations_ingestion_multiEntity_payloadConfig'
const SFTP_CONFIG_ENTITY_TYPE =
  '/1.0/entities/metadata/core_integrations_sftp_ingestion_config.json'
const CSV_IMPORT_ENTITY_TYPE = '/1.0/entities/metadata/csvImport.json'

const SOURCE_PASTE = 'PASTE'
const SOURCE_REPLAY = 'REPLAY'
const SOURCE_SFTP_LATEST = 'SFTP_LATEST'
const SOURCE_OPTIONS = [
  { label: 'Paste sample', value: SOURCE_PASTE },
  { label: 'Replay a past drop', value: SOURCE_REPLAY },
  { label: 'Pull latest from SFTP', value: SOURCE_SFTP_LATEST },
]

// [DEBUG][revert-before-merge] Local UI preview. When true, Run returns canned results instead of
// calling POST /1.0/entities/actions/ingestion/multi_entity/test, so the modal can be exercised on
// localhost before the dry-run endpoint is deployed. Revert this whole block before merge.
const DEBUG_USE_MOCK_RESULTS = false
const DEBUG_MOCK_REPORT = {
  rowCount: 3,
  failureCount: 1,
  rows: [
    {
      rowNum: 1,
      status: 'SUCCESS',
      entities: [
        {
          alias: 'appt',
          action: 'CREATE',
          entityId: '11111111-1111-1111-1111-111111111111',
          valid: true,
          validationErrors: [],
          entity: {
            uniqueId: '11111111-1111-1111-1111-111111111111',
            core_yms_appointment: { name: 'A123', trailer: 'TRL-9' },
          },
        },
        {
          alias: 'trailer',
          action: 'UPDATE',
          entityId: '22222222-2222-2222-2222-222222222222',
          valid: true,
          validationErrors: [],
          entity: { uniqueId: '22222222-2222-2222-2222-222222222222', core_asset: { name: 'TRL-9' } },
        },
      ],
      skips: {},
      errors: [],
    },
    {
      rowNum: 2,
      status: 'INVALID',
      entities: [
        {
          alias: 'appt',
          action: 'CREATE',
          entityId: '33333333-3333-3333-3333-333333333333',
          valid: false,
          validationErrors: [{ field: 'appointmentTime', message: 'is a required property' }],
          entity: { uniqueId: '33333333-3333-3333-3333-333333333333', core_yms_appointment: { name: 'B456' } },
        },
      ],
      skips: { trailer: 'SKIPPED - Entity does not exist, and is set to UPDATE_ONLY.' },
      errors: [],
    },
    {
      rowNum: 3,
      status: 'FAILED',
      entities: [],
      skips: {},
      errors: [
        { message: 'INGESTION_EXCEPTION - No schemas configured for alias foo, and could not derive any.' },
      ],
    },
  ],
}

interface IIngestionTestModalProps {
  entity: any
}

interface IIngestionTestModalState {
  configEdge: any
  source: string
  sample: string
  csvImportEdge: any
  runPreProcessor: boolean
  isRunning: boolean
  result: any
  error: string
}

/** Keeps a failed entity preview (e.g. an unresolved schema) from taking down the whole result list. */
class ResultErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }
  public static getDerivedStateFromError() {
    return { hasError: true }
  }
  public render() {
    if (this.state.hasError) {
      return <div className='c-placeholderColor'>(entity preview unavailable — see raw JSON)</div>
    }
    return this.props.children
  }
}

/**
 * Dry-run sandbox for a Multi Entity Ingestion Payload Config. Runs the draft config (as currently
 * edited, unsaved) against one of three input sources — a pasted sample, a past drop (CsvImport) for
 * the chosen SFTP config, or the latest file pulled read-only from SFTP — through the real ingestion
 * loop without writing anything, and shows, per row, the entities the config would produce, their
 * create/update action, and validation, each rendered as an entity preview alongside its raw JSON.
 */
export class IngestionTestModal extends React.Component<
  IIngestionTestModalProps,
  IIngestionTestModalState
> {
  public static open(props: IIngestionTestModalProps) {
    return OverlayManager.openOverlayElement(
      <Modal modalDialogClassName='c-modal-dialog--lg'>
        <IngestionTestModal {...props} />
      </Modal>,
    )
  }

  constructor(props: IIngestionTestModalProps) {
    super(props)
    this.state = {
      configEdge: null,
      source: SOURCE_PASTE,
      sample: '',
      csvImportEdge: null,
      runPreProcessor: true,
      isRunning: false,
      result: null,
      error: '',
    }
  }

  public render() {
    const { configEdge, source } = this.state
    return (
      <div className='c-modalBody u-innerBumper'>
        <h4 className='u-bumperBottom--sm'>Test Payload Config</h4>
        <p className='c-placeholderColor u-bumperBottom--sm'>
          Dry run: nothing is written. Shows the entities each input row would produce.
        </p>

        <UIEntitySelectField
          label='SFTP Ingestion Config'
          entityType={SFTP_CONFIG_ENTITY_TYPE}
          value={configEdge}
          onChange={(edge) => this.setState({ configEdge: edge })}
        />

        <UISelectField
          label='Input source'
          enumOptions={SOURCE_OPTIONS}
          optionLabelPath='label'
          optionValuePath='value'
          value={source}
          onChange={(value) => this.setState({ source: _.isString(value) ? value : value?.value })}
        />

        {this.renderSourceInput()}

        <Button isDisabled={!this.canRun()} onClick={this.handleRun}>
          {this.state.isRunning ? 'Running…' : 'Run'}
        </Button>

        {this.renderResult()}
      </div>
    )
  }

  private renderSourceInput() {
    const { source, sample, csvImportEdge } = this.state
    const configId = this.getConfigId()

    if (source === SOURCE_REPLAY) {
      return (
        <div className='u-bumperTop--sm u-bumperBottom--sm'>
          {configId ? (
            <UIEntitySelectField
              label='Past drop to replay'
              entityType={CSV_IMPORT_ENTITY_TYPE}
              filters={[{ type: 'match', path: 'creationSource.entityId', value: configId }]}
              orders={[{ path: 'creationDate', type: 'descending' }]}
              value={csvImportEdge}
              onChange={(edge) => this.setState({ csvImportEdge: edge })}
            />
          ) : (
            <div className='c-placeholderColor'>Select an SFTP Ingestion Config first.</div>
          )}
          <div className='c-placeholderColor u-bumperTop--xs'>
            The stored file was already munged at ingestion time, so this tests the entity, merge, and
            dedupe rules against real rows (the pre-processor is not re-run).
          </div>
        </div>
      )
    }

    if (source === SOURCE_SFTP_LATEST) {
      return (
        <div className='u-bumperTop--sm u-bumperBottom--sm'>
          <div className='c-placeholderColor u-bumperBottom--xs'>
            Reads a copy of the newest file in the configured SFTP source directory (read-only — the
            real ingestion job is unaffected) and runs it end to end.
          </div>
          {this.renderRunPreProcessor()}
        </div>
      )
    }

    // PASTE
    return (
      <div className='u-bumperTop--sm u-bumperBottom--sm'>
        <div className='u-bumperBottom--xs'>
          Sample payload (CSV: header of alias.path columns, then data rows)
        </div>
        <CodeMirrorInput
          value={sample}
          placeholder={'appt.name,appt.trailer\nA123,TRL-9'}
          onChange={(value) => this.setState({ sample: value })}
        />
        <div className='u-bumperTop--xs'>{this.renderRunPreProcessor()}</div>
      </div>
    )
  }

  private renderRunPreProcessor() {
    return (
      <label className='u-flex u-alignItems--center'>
        <input
          type='checkbox'
          checked={this.state.runPreProcessor}
          onChange={(e) => this.setState({ runPreProcessor: e.target.checked })}
        />
        <span className='u-bumperLeft--xs'>
          Run munging pre-processor (needed to test the property map)
        </span>
      </label>
    )
  }

  private canRun(): boolean {
    const { source, sample, isRunning } = this.state
    if (isRunning || !this.getConfigId()) {
      return false
    }
    if (source === SOURCE_PASTE) {
      return _.trim(sample).length > 0
    }
    if (source === SOURCE_REPLAY) {
      return !!this.getCsvImportId()
    }
    return true
  }

  private handleRun = () => {
    const { entity } = this.props
    const { source, sample, runPreProcessor } = this.state
    const ingestionConfigId = this.getConfigId()
    if (!ingestionConfigId) {
      return
    }
    const payloadConfig = entity.get(PAYLOAD_CONFIG_PATH) ?? _.get(entity.content, PAYLOAD_CONFIG_PATH)

    this.setState({ isRunning: true, result: null, error: '' })

    // [DEBUG][revert-before-merge] Short-circuit to canned results for local UI preview.
    if (DEBUG_USE_MOCK_RESULTS) {
      setTimeout(() => this.setState({ isRunning: false, result: DEBUG_MOCK_REPORT }), 400)
      return
    }

    apis
      .testIngestionPayloadConfig({
        ingestionConfigId,
        payloadConfig,
        source,
        sample: source === SOURCE_PASTE ? sample : undefined,
        csvImportId: source === SOURCE_REPLAY ? this.getCsvImportId() : undefined,
        runPreProcessor,
      })
      .then((result) => {
        const parsed = _.isString(result) ? JSON.parse(result) : result
        this.setState({ isRunning: false, result: parsed })
      })
      .catch((err) => {
        this.setState({
          isRunning: false,
          error: err?.responseText || err?.message || 'Test run failed.',
        })
      })
  }

  private getConfigId(): string | undefined {
    return this.edgeId(this.state.configEdge)
  }

  private getCsvImportId(): string | undefined {
    return this.edgeId(this.state.csvImportEdge)
  }

  private edgeId(value: any): string | undefined {
    const edge = _.isArray(value) ? value[0] : value
    return edge?.entityId
  }

  private renderResult() {
    const { result, error } = this.state
    if (error) {
      return <pre className='u-bumperTop c-errorColor'>{error}</pre>
    }
    if (!result) {
      return null
    }
    return (
      <div className='u-bumperTop'>
        <div className='u-bumperBottom--sm'>
          {result.rowCount} row(s), {result.failureCount} failed.
        </div>
        {(result.rows || []).map((row) => this.renderRow(row))}
      </div>
    )
  }

  private renderRow(row: any) {
    return (
      <div
        key={row.rowNum}
        className='u-bumperBottom--sm'
        style={{ borderTop: '1px solid #e0e0e0', paddingTop: 8 }}
      >
        <div>
          <strong>Row {row.rowNum}</strong> — {row.status}
        </div>
        {(row.entities || []).map((aliasResult) => this.renderAlias(aliasResult))}
        {this.renderSkips(row.skips)}
        {this.renderErrors(row.errors)}
      </div>
    )
  }

  private renderAlias(aliasResult: any) {
    const validationErrors = aliasResult.validationErrors || []
    const entityModel = this.toEntity(aliasResult.entity)
    return (
      <div key={aliasResult.alias} className='u-bumperLeft--sm u-bumperTop--xs'>
        <div>
          <code>{aliasResult.alias}</code> → {aliasResult.action}{' '}
          {aliasResult.valid ? '✓ valid' : '✗ invalid'}
        </div>
        {validationErrors.length > 0 && (
          <ul className='c-errorColor'>
            {validationErrors.map((err, i) => (
              <li key={i}>
                {err.field ? `${err.field}: ` : ''}
                {err.message}
              </li>
            ))}
          </ul>
        )}
        {entityModel && (
          <ResultErrorBoundary>
            <EntityPreviewCard entity={entityModel} isClickable={false} />
          </ResultErrorBoundary>
        )}
        <details>
          <summary className='c-placeholderColor'>raw JSON</summary>
          <CodeMirrorInput
            value={JSON.stringify(aliasResult.entity, null, 2)}
            language='json'
            onChange={_.noop}
          />
        </details>
      </div>
    )
  }

  private toEntity(content: any): Entity | null {
    if (!content) {
      return null
    }
    try {
      return new Entity(content, apis)
    } catch {
      return null
    }
  }

  private renderSkips(skips: any) {
    const entries = _.toPairs(skips || {})
    if (entries.length === 0) {
      return null
    }
    return (
      <ul className='u-bumperLeft--sm c-placeholderColor'>
        {entries.map(([alias, message]) => (
          <li key={alias}>
            {alias}: {message as string}
          </li>
        ))}
      </ul>
    )
  }

  private renderErrors(errors: any[]) {
    if (!errors || errors.length === 0) {
      return null
    }
    return (
      <ul className='u-bumperLeft--sm c-errorColor'>
        {errors.map((err, i) => (
          <li key={i}>{err.message}</li>
        ))}
      </ul>
    )
  }
}
